// @flow

import config from 'config/app.config';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import styled from 'styled-components';
import { Licenses } from 'types/common';

import { getLang } from '../../../i18n/l10n';
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- Used in JSX below
import { ProgressBar, ButtonBack, useScroll } from '../../../modules/shared';
import type { RootState } from '../../../store/types';
import { colorPalette, media, mediaBreakpoints, timings } from '../../../styles/GlobalStyle';
import { checkGoBack } from '../../../utils/CommonUtils';
import { pageWithoutHeader } from '../../../utils/LayoutUtils';
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- Used in JSX below
import { LandingHeader } from '../../landing/components/LandingHeader';
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- Used in JSX below
import CloseAppButton from '../../shared/components/CloseAppButton';
import Spacer from '../../shared/components/Spacer';
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- Used in JSX below
import PromoBannerWithInfoSheet from '../../step-user-creation/components/PromoBannerWithInfoSheet';
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- Used in JSX below
import WelcomeBonusModalInfo from '../../step-user-creation/components/WelcomeBonusModalInfo';

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- Used in JSX below
import HeaderByLicenses from './HeaderByLicenses';
import LangSwitcher from './LangSwitcher';

type HeaderRawProps = {
  className: string,
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- Used in JSX below
const HeaderElement = styled.header`
  position: absolute;
  width: 100%;
  max-width: ${mediaBreakpoints.wide};
  background-color: ${colorPalette.dark.active};
  transition: transform ${timings.shadow};
  z-index: 3;

  ${media.greaterThan('desktop')`
  position: fixed;
  top: 0;
  transform: translateX(-50%);
  left: 50%;
`}

  &.header--form {
    max-width: none;
    z-index: 3;
    height: 70px;
    ${media.greaterThan('desktop')`
    height: 60px;
    &.no-disclaimer {
      height: 8px;
    }
  `}
    &:before {
      display: none;
    }
  }

  &.header--sticky {
    position: fixed;
    top: 0;
    transform: translateY(-100%);
    opacity: 0;
    transition: all 300ms;
    &.visible {
      transform: translateY(0%);
      opacity: 1;
    }
    &:before {
      display: block;
    }
    ${media.greaterThan('desktop')`
    display: none;
  `}
  }

  &:before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    display: block;
    width: 100%;
    height: 60px;
    background: linear-gradient(180deg, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0) 100%);
    transition: opacity ${timings.shadow}, transform 0ms ${timings.shadow};
    user-select: none;
    opacity: 0;
  }

  ${media.greaterThan('desktop')`
  height: 80px;
`}

  ${p =>
    p.scrolled
    && `
  &:before {
    transition: opacity ${timings.shadow}, transform 0ms;
    transform: translateY(100%);
    opacity: 1;
  }
`}

  ${media.greaterThan('desktop')`

  ${p =>
    p.scrolled
    && `
    transform: translate(-50%, -60px);
  `}
`}
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- Used in JSX below
const HeaderMobile = styled.div`
  position: relative;
  display: flex;
  align-items: baseline;
  padding: 25px 25px 25px calc(20px + 1.7rem);
  ${media.greaterThan('desktop')`
    display: none;
  `}
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- Used in JSX below
const NoHeader = styled.header`
  display: none;
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars -- Used in JSX below
const StyledLangSwitcher = styled(LangSwitcher)`
  position: absolute;
  margin-top: -2px;
  right: calc(20px + 1.7rem);
`;

export const Header = ({ className }: HeaderRawProps) => {
  const { y } = useScroll();
  const scrolled = y > 0;
  const common = useSelector((state: RootState) => state.common);
  const idHasBeenScanned = common && common.identityScan && !!Object.keys(common.identityScan).length;
  const shouldDisplayGoBack = checkGoBack(common.step, common.subStep, idHasBeenScanned);
  const shouldNotDisplayHeaderMobile = (common.step === 1 && config.license === Licenses.FR)
    || (common.step === 11 && config.license === Licenses.FR);

  const history = useHistory();
  const isLandingPage = history.location.pathname.startsWith('/landing') || common.step === 0;

  const isNoHeaderPage = pageWithoutHeader.some(page => history.location.pathname === page);

  if (isNoHeaderPage) {
    return (
      <>
        <NoHeader />
      </>
    );
  }

  if (isLandingPage) {
    return <LandingHeader />;
  }

  return (
    <>
      <HeaderElement
        scrolled={scrolled}
        className={`${className} header--form${getLang() !== 'fr' ? ' no-disclaimer' : ''}`}
      >
        <HeaderByLicenses />
        {(common.step < 10 || common.step === 11) && (
          <>
            <ProgressBar currentStep={1} />
            {shouldNotDisplayHeaderMobile ? (
              <>
                {common.step === 1 && <StyledLangSwitcher />}
              </>
            ) : (
              <HeaderMobile>
                {shouldDisplayGoBack && <ButtonBack arrowOnly />}
                <h2>{common.titleMobile}</h2>
                {common.step === 1 && <StyledLangSwitcher />}
              </HeaderMobile>
            )}
          </>
        )}
        <CloseAppButton />
      </HeaderElement>

      <HeaderElement
        scrolled={scrolled}
        className={`${className} header--form header--sticky${
          y > window.innerHeight + 30 ? ' visible' : ''
        }`}
      >
        {(common.step < 10 || common.step === 11) && (
          <>
            <ProgressBar currentStep={1} />
            {shouldNotDisplayHeaderMobile ? (
              <>
                {common.step === 1 && <StyledLangSwitcher />}
              </>
            ) : (
              <HeaderMobile>
                {shouldDisplayGoBack && <ButtonBack arrowOnly />}
                <h2>{common.titleMobile}</h2>
                {common.step === 1 && <StyledLangSwitcher />}
              </HeaderMobile>
            )}
          </>
        )}
      </HeaderElement>
    </>
  );
};
